import React from 'react';
import { Link } from 'react-router-dom';
import ImageLoader from '../components/ImageLoader';
import ClickableImage from '../components/ClickableImage';
import { restaurantImages, etangsImages, generalImages } from '../constants/imagePlaceholders';
import SEO from '../components/SEO';

const HomePage: React.FC = () => {
  return (
    <div>
      <SEO 
        title="Accueil"
        description="Restaurant et étangs de pêche au cœur du Morvan. Cuisine locale avec spécialités de poissons d'eau douce et étangs aménagés pour tous types de pêche."
        canonicalUrl="https://lemoulintala.fr/"
        ogImageUrl="/og-image.jpg"
        keywords="restaurant Morvan, étangs de pêche, Moulin Tala, cuisine locale, poisson eau douce"
      />
      
      {/* Hero Section */}
      <section 
        className="text-white min-h-screen flex items-center relative overflow-hidden bg-primary"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0 h-full w-full"
        >
          <ClickableImage 
            src={generalImages.vue1}
            alt="Moulin Tala - Vue panoramique"
            title="Vue panoramique du Moulin Tala"
            description="Notre domaine niché au cœur du Parc Naturel Régional du Morvan"
            className="w-full h-full"
            rounded={false}
            hoverEffect={false}
            aspectRatio="auto"
          />
          <div 
            className="absolute inset-0 bg-gradient-to-b from-black/40 to-primary/70 z-10" 
          ></div>
        </div>
        
        <div 
          className="max-w-screen-xl mx-auto px-6 relative z-20 w-full" 
        >
          <div 
            className="max-w-2xl" 
          >
            <h1 
              className="text-5xl lg:text-6xl font-bold mb-6 leading-tight font-heading text-shadow-md" 
            >
              Moulin Tala
            </h1>
            <p 
              className="text-xl lg:text-2xl mb-10 leading-relaxed font-body max-w-[90%] text-shadow-sm" 
            >
              Restaurant & étangs de pêche au cœur du Morvan
            </p>
            <div 
              className="flex gap-4 flex-wrap"
            >
              <Link 
                to="/restaurant" 
                className="inline-block bg-accent text-dark py-3 px-7 rounded-full font-bold font-body transition duration-300 ease-in-out shadow-md cursor-pointer hover:bg-accent/90 no-underline"
              >
                Restaurant
              </Link>
              <Link 
                to="/etangs" 
                className="inline-block bg-white/15 backdrop-blur-md text-white py-3 px-7 rounded-full font-bold font-body transition duration-300 ease-in-out border-2 border-white shadow-sm cursor-pointer hover:bg-white/25 no-underline"
              >
                Pêcher chez nous
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section 
        className="py-24 bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.05)] relative"
      >
        <div 
          className="max-w-screen-xl mx-auto px-6 text-center"
        >
          <div className="inline-block relative mb-6"> 
            <h2 
              className="text-3xl lg:text-4xl font-bold text-primary font-heading pb-4" 
            >
              Bienvenue au Moulin Tala
            </h2>
            <span 
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-accent rounded"
            ></span>
          </div>
          <p 
            className="text-lg leading-loose max-w-3xl mx-auto mb-8 text-dark font-body"
          >
            Situé au cœur du Morvan, le Moulin Tala vous accueille dans un cadre naturel exceptionnel. 
            Notre établissement combine un restaurant de poissons de qualité et des étangs de pêche pour 
            les passionnés et les familles.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section 
        className="py-24 relative"
      >
        <div 
          className="max-w-screen-xl mx-auto px-6"
        >
          <div className="relative text-center mb-12">
            <h2 
              className="text-3xl lg:text-4xl font-bold text-primary font-heading pb-4 inline-block"
            >
              Nos Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1: Restaurant */}
            <div 
              className="bg-white rounded-xl shadow-md transition duration-300 ease-in-out h-full flex flex-col justify-between border border-black/5 overflow-hidden p-8 hover:shadow-lg"
            >
              <div>
                <div 
                  className="bg-gray-100 rounded-lg overflow-hidden relative mb-6"
                >
                  <ClickableImage 
                    src={restaurantImages.exterior} 
                    alt="Restaurant - Extérieur"
                    title="Restaurant du Moulin Tala"
                    description="Restaurant avec vue sur les étangs"
                    aspectRatio="16/9"
                  />
                </div>
                <h3 
                  className="text-2xl font-bold mb-3 text-secondary font-heading"
                >
                  Restaurant
                </h3>
                <p 
                  className="text-darkGray mb-6 leading-relaxed font-body text-base"
                >
                  Simple et sympathique, vous pourrez déguster des truites aux amandes, des truites fumées, et d'autres spécialités.
                </p>
              </div>
              <Link 
                to="/restaurant" 
                className="inline-block self-start bg-accent text-dark py-2 px-5 rounded-full font-bold font-body text-sm transition duration-300 ease-in-out shadow-md cursor-pointer hover:bg-accent/90 no-underline"
              >
                Voir le restaurant
              </Link>
            </div>

            {/* Card 2: Etangs */}
            <div 
              className="bg-white rounded-xl shadow-md transition duration-300 ease-in-out h-full flex flex-col justify-between border border-black/5 overflow-hidden p-8 hover:shadow-lg"
            >
              <div>
                <div 
                  className="bg-gray-100 rounded-lg overflow-hidden relative mb-6"
                >
                  <ClickableImage 
                    src={etangsImages.grandEtang1} 
                    alt="Grand étang de pêche"
                    title="Grand étang pour la pêche à la carpe"
                    description="Pêchez la carpe dans notre grand étang aménagé"
                    aspectRatio="16/9"
                  />
                </div>
                <h3 
                  className="text-2xl font-bold mb-3 text-primary font-heading">
                  Étangs de Pêche
                </h3>
                <p 
                  className="text-darkGray mb-6 leading-relaxed font-body text-base">
                  Profitez de nos étangs aménagés pour la pêche à la truite. Vous trouverez sur place : appâts, matériel de dépannage (hameçons, bouchons, plombs) et des cannes en location.
                </p>
              </div>
              <Link 
                to="/etangs" 
                className="inline-block self-start bg-accent text-dark py-2 px-5 rounded-full font-bold font-body text-sm transition duration-300 ease-in-out shadow-md cursor-pointer hover:bg-accent/90 no-underline"
              >
                Découvrir les étangs
              </Link>
            </div>

            {/* Card 3: Cadre Naturel */}
            <div 
              className="bg-white rounded-xl shadow-md transition duration-300 ease-in-out h-full flex flex-col justify-between border border-black/5 overflow-hidden p-8 hover:shadow-lg"
            >
              <div>
                <div 
                  className="bg-gray-100 rounded-lg overflow-hidden relative mb-6"
                >
                  <ClickableImage 
                    src={generalImages.vue2} 
                    alt="Cadre naturel Morvan - vue 2"
                    title="Cadre naturel exceptionnel"
                    description="Immergez-vous dans la beauté du Morvan"
                    aspectRatio="16/9"
                  />
                </div>
                 <h3 
                   className="text-2xl font-bold mb-3 text-secondary font-heading">
                  Cadre Exceptionnel
                </h3>
                <p 
                  className="text-darkGray mb-6 leading-relaxed font-body text-base">
                  Immergez-vous dans la beauté du Morvan. Un lieu idéal pour se détendre, pêcher, et savourer un bon repas en pleine nature.
                </p>
              </div>
               <Link 
                to="/galerie" 
                className="inline-block self-start bg-accent text-dark py-2 px-5 rounded-full font-bold font-body text-sm transition duration-300 ease-in-out shadow-md cursor-pointer hover:bg-accent/90 no-underline"
              >
                Voir la galerie
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section 
        className="py-20 bg-primary text-white relative" 
      >
        <div 
          className="max-w-screen-xl mx-auto px-6 text-center relative z-10"
        >
          <h2 
            className="text-3xl lg:text-4xl font-bold mb-6 font-heading text-accent"
          >
            Prêt à nous Rendre Visite ?
          </h2>
          <p 
            className="text-lg leading-relaxed mb-10 max-w-2xl mx-auto font-body opacity-90"
          >
            Réservez votre table au restaurant ou planifiez votre journée de pêche. Nous sommes impatients de vous accueillir au Moulin Tala.
          </p>
          <div 
            className="flex gap-4 flex-wrap justify-center"
          >
            <Link 
              to="/contact" 
              className="inline-block bg-accent text-dark py-3 px-7 rounded-full font-bold font-body transition duration-300 ease-in-out shadow-md cursor-pointer hover:bg-accent/90 no-underline"
            >
              Nous Contacter
            </Link>
             <Link 
              to="/tarifs" 
              className="inline-block bg-transparent text-white py-3 px-7 rounded-full font-bold font-body transition duration-300 ease-in-out border-2 border-white cursor-pointer hover:bg-white/10 no-underline"
             >
              Voir les Tarifs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 